import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_PATH } from "../environment";

// ✅ Consistent endpoint for Lead Stats API
const BASE_URL = `${API_BASE_PATH}/lead-stats`;

export const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken") || "";
  return {
    Authorization: token,
    Accept: "application/json",
  };
};

export interface LeadStageCount {
  lead_stage_id: string;
  lead_stage_name: string;
  total_leads: number;
}

export interface LeadStageDetail extends LeadStageCount {
  company_name: string;
  company_mail: string;
  lead_owner_name: string;
}

export interface TargetDetail {
  id: number;
  user_id: string;
  user_name: string;
  salary?: number;
  target?: number;
  incentive_target?: number;
  total_deal_value?: number;
  incentive_achieved_amount?: number;
  incentive_percentage?: number;
  incentive_level?: string;
  calculated_incentive?: number;
  created_at?: string;
}

export interface MonthlyLeadDealStats {
  contacts_created_this_month: number;
  leads_created_this_month: number;
  deals_created_this_month: number;
  deals_won_this_month: number;
  total_leads_closed: number;
}

export interface LeadFullDetail {
  company_name: string;
  LeadSource_id: string;
  rate_per_login: number | null;
  total_no_login: number | null;
  proposal_id: string | null;
  website_url: string;
  LeadIndustry_id: string;
  tax: number | null;
  cgst: number | null;
  revision_id: string | null;
  lead_id: string;
  LeadStage_id: string;
  amount: number | null;
  quotaiton_id: string | null;
  leadowner_id: string;
  leadowner_name: string;
  lead_description: string;
  contact_id: string;
  total_amount: number | null;
  sgst: number | null;
  client_id: string;
  lead_status: string;
  product_id: string;
  proposal_discount: number | null;
  igst: number | null;
  branch_id: string;
  lead_value: string;
  product_type_id: string;
  proposal_discount_percentage: number | null;
  cgst_percentage: number | null;
  created_at: string;
  Currency: string;
  customerPotential_id: string;
  proposal_tax_percentage: number | null;
  sgst_percentage: number | null;
  updated_at: string | null;
  leadtype_id: string;
  no_logins: number | null;
  client_proposal_number: string | null;
  igst_percentage: number | null;
}

export interface LeadCountsFunnel {
  total_leads?: number;
  not_contacted_leads?: number;
  contacted_leads?:number;
  closed_leads?: number;
  deals_won?: number;
 sortedData: { name: string; value: number; percentage?: number }[];
}

export interface DealStatusCount {
  total_deal?: number;
  total_deals_closed?: number;
  total_deals_open?: number;
  total_deals_lost?: number;
  total_deals_won?: number;
}

export interface TotalDealSource {
  deal_source_id: string;
  deal_source_name: string;
  total_deal: number;
}

export interface LeadStats {
  lead_counts: {
    total_leads: number;
    active_leads: number;
  };
  monthly_closed_values: {
    year?: number;
    month: number;
    closed_value: number;
  }[];
  overall_lead_value_by_status_WITH_COUNT: {
    status: string;
    lead_count: number;
    total_value: number;
  }[];
  lead_to_deal_conversion_rate: {
    conversion_rate_percent: string;
  };
  leads_closed_by_day_and_month: {
    month: number;
    day: number;
    closed_leads: number;
  }[];
  total_lead_value_by_month: {
    month: number;
    year?: number;
    lead_value?: number;
    total_lead_value?: number;
  }[];
  total_leads_by_source: {
    lead_source_id?: string | null;
    leadsource_name:string;
    lead_source_name: string;
    total_leads: number;
  }[];
  get_leads_by_status_full_details: LeadFullDetail[];
  get_leads_by_status_full_details_not_cntacted: LeadFullDetail[];
  get_all_targets: {
    data: TargetDetail[];
  };
  get_monthly_count_of_lead_and_deal: MonthlyLeadDealStats;
  get_total_leads_by_all_stage: LeadStageCount[];
  get_total_leads_by_all_stage_withdetails: LeadStageDetail[];
  get_lead_counts_funnel: LeadCountsFunnel[];
  get_total_dealsource: TotalDealSource[];
  get_dealstatuscount: DealStatusCount[];
}
export interface FunnelItem {
  name: string;
  value: number;
  percentage?: number;
}
export interface SeriesData {
  name: string;
  data: number[];
}

// ✅ Fetch Lead Stats
export const fetchLeadStats = async ({
  signal,
}: { signal?: AbortSignal } = {}): Promise<LeadStats> => {
  const { data } = await axios.get<LeadStats>(BASE_URL, {
    headers: getAuthHeaders(),
    signal, // 👈 allows query cancellation
  });
  return data;
};

export const timeAgo = (dateString: string | null | undefined): string => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "N/A";
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (seconds < 60) return `${seconds} second${seconds !== 1 ? "s" : ""} ago`;
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? "s" : ""} ago`;
  if (hours < 24) return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
  return `${days} day${days !== 1 ? "s" : ""} ago`;
};
